import { PrintReceipts, PrintStyles } from '@/components/PrintReceipts'
import { usePrintDispatch } from '@/hooks/usePrintDispatch'
import { useRole } from '@/hooks/useRole'
import { Order, Printer, SharedProps } from '@/types'
import { formatCurrency, getCustomerInfo } from '@/utils/format'
import { Head, router, usePage } from '@inertiajs/react'
import { ActionIcon, Badge, Button, Group, Menu, Paper, Stack, Table, Text, TextInput, Title } from '@mantine/core'
import { IconPrinter, IconX } from '@tabler/icons-react'
import axios from 'axios'
import dayjs from 'dayjs'
import { useMemo } from 'react'

function PrinterMenuItem({ printer, onPrint }: { printer: Printer; onPrint: (printerId: number) => void }) {
  const handleClick = () => onPrint(printer.id)

  return (
    <Menu.Item leftSection={<IconPrinter size={16} />} onClick={handleClick}>
      {printer.name}
    </Menu.Item>
  )
}

export default function PaymentsIndex({
  date,
  orders,
  printers,
  receipts_html,
}: SharedProps & { date: string; orders: Order[]; printers: Printer[]; receipts_html: string[] }) {
  const { current_organization } = usePage<SharedProps>().props
  const { isAdmin } = useRole()

  const pageTitle = 'Z de Caisse'
  const organizationSlug = current_organization?.slug || ''

  const handleDateChange = (newDate: string) =>
    router.get(`/organizations/${organizationSlug}/payments`, { date: newDate }, { preserveState: true })

  const handleResetDate = () => handleDateChange(dayjs().format('YYYY-MM-DD'))

  const sendPrintJobs = (printerId: number | null) =>
    axios.post(`/organizations/${organizationSlug}/payments/print`, {
      date,
      printer_id: printerId,
      replace_pending_jobs: true,
    })

  const { cloudprntEnabled, printingPrinterId, printBrowser, printToPrinter } = usePrintDispatch({ send: sendPrintJobs })

  const printing = printingPrinterId !== undefined
  const showPrinterMenu = cloudprntEnabled && printers.length > 1
  const handleDefaultPrint = () =>
    cloudprntEnabled ? printToPrinter(printers[0]?.id ?? null) : printBrowser()

  const summary = useMemo(() => {
    const grouped: Record<
      string,
      { user_name: string; order_count: number; cash_total: number; card_total: number; total: number }
    > = {}

    for (const order of orders) {
      const key = order.cashier?.name || 'Inconnu'
      if (!grouped[key]) grouped[key] = { user_name: key, order_count: 0, cash_total: 0, card_total: 0, total: 0 }
      const price = Number(order.price)
      grouped[key].order_count++
      grouped[key].total += price
      if (order.payment_method === 'cash') grouped[key].cash_total += price
      if (order.payment_method === 'card') grouped[key].card_total += price
    }

    return Object.values(grouped)
  }, [orders])

  const grandCount = orders.length
  const grandCash = summary.reduce((accumulator, row) => accumulator + row.cash_total, 0)
  const grandCard = summary.reduce((accumulator, row) => accumulator + row.card_total, 0)
  const grandTotal = summary.reduce((accumulator, row) => accumulator + row.total, 0)

  return (
    <>
      <Head title={`${pageTitle} - ${current_organization?.name}`} />
      <PrintStyles />

      <Stack gap="md" className="no-print">
        <Group justify="space-between" align="center">
          <Title>{pageTitle}</Title>
          <Group gap="sm">
            {showPrinterMenu ? (
              <Menu position="bottom-end">
                <Menu.Target>
                  <Button
                    variant="light"
                    leftSection={<IconPrinter size={18} />}
                    loading={printing}
                    disabled={orders.length === 0}
                  >
                    Imprimer
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  {printers.map((printer) => (
                    <PrinterMenuItem key={printer.id} printer={printer} onPrint={printToPrinter} />
                  ))}
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Button
                variant="light"
                leftSection={<IconPrinter size={18} />}
                onClick={handleDefaultPrint}
                loading={printing}
                disabled={orders.length === 0}
              >
                Imprimer
              </Button>
            )}
            {isAdmin && (
              <TextInput
                type="date"
                value={date}
                onChange={(event) => handleDateChange(event.target.value)}
                w={180}
                rightSection={
                  date !== dayjs().format('YYYY-MM-DD') ? (
                    <ActionIcon size="sm" variant="subtle" color="gray" onClick={handleResetDate}>
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
              />
            )}
          </Group>
        </Group>

        <Text size="lg" fw={500}>
          {dayjs(date).format('dddd D MMMM YYYY')}
        </Text>

        <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
          <Table.ScrollContainer minWidth={600}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Utilisateur</Table.Th>
                  <Table.Th w={120}>Nb Commandes</Table.Th>
                  <Table.Th w={140}>Espèces</Table.Th>
                  <Table.Th w={140}>Carte</Table.Th>
                  <Table.Th w={140}>Total</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {summary.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed" ta="center" py="md">
                        Aucun encaissement pour cette date
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
                {summary.map((row) => (
                  <Table.Tr key={row.user_name}>
                    <Table.Td fw={500}>{row.user_name}</Table.Td>
                    <Table.Td>{row.order_count}</Table.Td>
                    <Table.Td>{formatCurrency(row.cash_total)}</Table.Td>
                    <Table.Td>{formatCurrency(row.card_total)}</Table.Td>
                    <Table.Td fw={600}>{formatCurrency(row.total)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
              {summary.length > 0 && (
                <Table.Tfoot>
                  <Table.Tr style={{ borderTop: '2px solid var(--mantine-color-dark-4)' }}>
                    <Table.Td fw={700}>Total</Table.Td>
                    <Table.Td fw={700}>{grandCount}</Table.Td>
                    <Table.Td fw={700}>{formatCurrency(grandCash)}</Table.Td>
                    <Table.Td fw={700}>{formatCurrency(grandCard)}</Table.Td>
                    <Table.Td fw={700}>{formatCurrency(grandTotal)}</Table.Td>
                  </Table.Tr>
                </Table.Tfoot>
              )}
            </Table>
          </Table.ScrollContainer>
        </Paper>

        {orders.length > 0 && (
          <Paper withBorder radius="md" p={{ base: 0, sm: 'md' }}>
            <Table.ScrollContainer minWidth={700}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={70}>N°</Table.Th>
                    <Table.Th>Client</Table.Th>
                    <Table.Th w={100}>Méthode</Table.Th>
                    <Table.Th w={120}>Montant</Table.Th>
                    <Table.Th w={80}>Heure</Table.Th>
                    <Table.Th>Encaissé par</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {orders.map((order) => (
                    <Table.Tr
                      key={order.id}
                      onClick={() => router.get(`/organizations/${organizationSlug}/orders/${order.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Td>#{order.id}</Table.Td>
                      <Table.Td>{getCustomerInfo(order).name}</Table.Td>
                      <Table.Td>
                        <Badge variant="light" color={order.payment_method === 'card' ? 'blue' : 'green'}>
                          {order.payment_method === 'card' ? 'Carte' : 'Espèces'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{formatCurrency(order.price)}</Table.Td>
                      <Table.Td>{dayjs(order.paid_at).format('HH:mm')}</Table.Td>
                      <Table.Td>{order.cashier?.name || 'Inconnu'}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}
      </Stack>

      <PrintReceipts receipts_html={receipts_html} />
    </>
  )
}
